import React, { useEffect, useState } from "react";
import styles from "./HomeStats.module.css";
import { getLatestStats } from "../../services/stats";
import { Stats } from "../../types/stats";

/** Default snapshot for first render */
const defaultStats: Stats = {
  asOf: new Date().toISOString(),
  global: {
    members: "340K+",
    chapters: "11,300+",
    countries: "76",
    referrals: "17.4 Million",
    businessGenerated: "$26.4 Billion",
  },
  india: {
    members: "69,894",
    chapters: "1,428",
    cities: "131",
    referrals: "47,91,582",
    businessGenerated: "₹50,396 Cr",
    avgSeatValue: "₹75.78 Lakh",
  },
  patna: {
    members: "134",
    chapters: "03",
    referrals: "8,921",
    visitors: "1,786+",
    oneToOne: "16,438",
    businessGenerated: "₹218 Cr",
    businessLast12Months: "₹66.19 Cr",
  },
};

export default function HomeStats() {
  const [stats, setStats] = useState<Stats>(defaultStats);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const res = await getLatestStats();
        if (mounted && res?.success && res.data) {
          setStats(res.data);
        }
      } catch (err) {
        console.error("HomeStats load error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className={styles.wrapper}>
      {/* Header */}
      <div className={styles.topHeader}>
        <h2 className={styles.mainTitle}>BNI Performance Snapshot</h2>
        <p className={styles.asOf}>
          As of {new Date(stats.asOf).toLocaleDateString("en-GB")}
          {loading ? " (loading…)" : ""}
        </p>
      </div>

      {/* === GLOBAL === */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>🌍 Global Statistics</h3>
        <div className={styles.grid}>
          <Stat label="Members" value={stats.global.members} />
          <Stat label="Chapters" value={stats.global.chapters} />
          <Stat label="Countries" value={stats.global.countries} />
          <Stat label="Referrals" value={stats.global.referrals} />
          <Stat
            label="Business Generated"
            value={stats.global.businessGenerated}
            full
          />
        </div>
      </div>

      {/* === INDIA === */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>🇮🇳 India Statistics</h3>
        <div className={styles.grid}>
          <Stat label="Members" value={stats.india.members} />
          <Stat label="Chapters" value={stats.india.chapters} />
          <Stat label="Cities" value={stats.india.cities} />
          <Stat label="Referrals" value={stats.india.referrals} />
          <Stat
            label="Business (Last 12 Months)"
            value={stats.india.businessGenerated}
          />
          <Stat label="Avg Seat Value" value={stats.india.avgSeatValue} full />
        </div>
      </div>

      {/* === PATNA === */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>🏙️ BNI Patna</h3>
        <div className={styles.grid}>
          <Stat label="Members" value={stats.patna.members} />
          <Stat label="Chapters" value={stats.patna.chapters} />
          <Stat label="Referrals" value={stats.patna.referrals} />
          <Stat label="Visitors" value={stats.patna.visitors} />
          <Stat label="1-2-1 Meetings" value={stats.patna.oneToOne} />
          <Stat
            label="Business Generated"
            value={stats.patna.businessGenerated}
          />
          <Stat
            label="Business (Last 12 Months)"
            value={stats.patna.businessLast12Months}
            full
          />
        </div>
      </div>
    </section>
  );
}

type StatProps = {
  label: string;
  value: string;
  full?: boolean;
};

function Stat({ label, value, full }: StatProps | any) {
  return (
    <div className={`${styles.card} ${full ? styles.fullRow : ""}`}>
      <div className={styles.value}>{value}</div>
      <div className={styles.label}>{label}</div>
    </div>
  );
}
